import { defineStore, acceptHMRUpdate } from 'pinia'
import { getYieldDataFromTask, getLatestWorkDataFromField } from '@/scripts/task'
import { useMaps } from './maps'
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { ScatterplotLayer } from '@deck.gl/layers'
import { makeYieldColorFnFromPoints } from '@/helpers/yieldMapBuckets'
import { useIsoPreview } from './isoPreview'
import { useAsAppliedMap } from './asAppliedMap'
import { useFieldLayersStore } from './fieldLayers'
import { useDemoStore } from './demoStore'
import i18n from '@/i18n'

const DDI_YIELD = 84 // Represents the DDI value for the yield

const WORK_TYPE_LABEL_KEYS = {
    harvest: 'Harvest',
    seeding: 'Seeding',
    tillage: 'Tillage',
    spraying: 'Spraying',
    fertilizing: 'Fertilizing',
    slurry: 'Slurry',
}

export const useYieldMap = defineStore('yieldMap', {
    state: () => ({
        isUnmounted: false,
        yieldData: null,
        yieldHeatmapOverlay: null,
        yieldHeatmapLayer: null,
        yieldHeatmapVisible: false,
        isYieldLoading: false,
        yieldColors: [],
        legendDraggable: null,
    }),
    actions: {
        clearOverlaysBeforeYield() {
            const isoPreview = useIsoPreview()
            const aam = useAsAppliedMap()

            if (isoPreview.previewPayload) {
                isoPreview.clearPreview(true)
            }
            aam.clearMap(true)
        },
        translatedWorkType(workType) {
            const key = WORK_TYPE_LABEL_KEYS[workType] || workType
            return i18n.global.t(key)
        },
        async showYieldForTask(taskId) {
            const demo = useDemoStore()
            if (demo.isBlocked('yieldmap')) return

            if (this.isUnmounted) return

            this.isYieldLoading = true
            this.clearOverlaysBeforeYield()

            try {
                const res = await getYieldDataFromTask(taskId)
                if (this.isUnmounted) return

                if (!res?.ok) {
                    return {
                        ok: false,
                        status: res?.status,
                        data: {
                            info: res?.data?.info || i18n.global.t('No yield data available'),
                        },
                    }
                }
                this.yieldData = res.data
                // At the moment we don't want to display anything on the map except "yield".
                // The backend returns but we don't display it for now.
                if (this.yieldData?.legend?.ddi !== DDI_YIELD) {
                    this.yieldData = null
                    return {
                        ok: false,
                        status: 404,
                        data: {
                            info: i18n.global.t('No yield data available for task') + ' ' + taskId,
                        },
                    }
                }

                if (this.isUnmounted) return

                this.renderYieldOnMap()

                return {
                    ok: true,
                    data: {
                        info: i18n.global.t('Yield data shown successfully for task') + ' ' + taskId,
                    },
                }
            } finally {
                this.isYieldLoading = false
            }
        },
        async showLastFieldWork(fieldId, workType) {
            const demo = useDemoStore()
            if (demo.isBlocked('latest_work_data')) return

            if (this.isUnmounted) return

            this.isYieldLoading = true
            this.clearOverlaysBeforeYield()

            const workTypeLabel = this.translatedWorkType(workType)

            try {
                const res = await getLatestWorkDataFromField(fieldId, workType)
                if (this.isUnmounted) return

                if (!res?.ok) {
                    return {
                        ok: false,
                        status: res?.status,
                        data: {
                            info: res?.data?.info || i18n.global.t(
                                'No {workType} data available for field {fieldId}',
                                { workType: workTypeLabel, fieldId }
                            ),
                        },
                    }
                }

                this.yieldData = res.data

                if (this.isUnmounted) return

                this.renderYieldOnMap()

                return {
                    ok: true,
                    data: {
                        info: i18n.global.t(
                            'Last {workType} data shown for field {fieldId}',
                            { workType: workTypeLabel, fieldId }
                        ),
                    },
                }
            } finally {
                this.isYieldLoading = false
            }
        },
        renderYieldOnMap() {
            if (this.isUnmounted) return false

            const mapStore = useMaps()
            const fls = useFieldLayersStore()

            if (!mapStore.map || !mapStore.google || !this.yieldData || !Array.isArray(this.yieldData.points)) {
                return false
            }

            fls.toggleFieldLayersVisibility(false)

            const points = this.yieldData.points
            const getColor = makeYieldColorFnFromPoints(points)
            this.yieldColors.push(getColor)
            const layer = new ScatterplotLayer({
                id: 'yield-points-layer',
                data: points,
                getPosition: (d) => d.coordinates,
                getFillColor: (d) => getColor(d.value),
                getRadius: 5,
                radiusUnits: 'meters',
                filled: true,
                stroked: false,
                pickable: false,
            })

            if (!this.yieldHeatmapOverlay) {
                this.yieldHeatmapOverlay = new GoogleMapsOverlay({
                    layers: [layer],
                    controller: false,
                    style: { zIndex: 1000 },
                })
                this.yieldHeatmapOverlay.setMap(mapStore.map)
            } else {
                this.yieldHeatmapOverlay.setProps({
                    layers: [layer],
                })
            }

            this.yieldHeatmapLayer = layer
            this.yieldHeatmapVisible = true

            const bounds = new mapStore.google.maps.LatLngBounds()
            points.forEach((p) => {
                if (Array.isArray(p.coordinates) && p.coordinates.length === 2) {
                    bounds.extend({
                        lat: p.coordinates[1],
                        lng: p.coordinates[0],
                    })
                }
            })
            if (!bounds.isEmpty()) {
                mapStore.map.fitBounds(bounds)
            }
            return true
        },
        clearYieldHeatmap(removeLegend = false) {
            const fls = useFieldLayersStore()

            if (this.yieldHeatmapOverlay) {
                this.yieldHeatmapOverlay.setMap(null)
            }
            this.yieldHeatmapOverlay = null
            this.yieldHeatmapLayer = null
            this.yieldHeatmapVisible = false
            this.yieldData = null
            this.yieldColors = []
            if (removeLegend && this.legendDraggable) {
                this.legendDraggable.disable()
            }
            fls.toggleFieldLayersVisibility(true)
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useYieldMap, import.meta.hot))
}
